// 원서 워크북 채점과 해설지용 기록. 화면(BookWorkbookPage)과 떨어진 순수 함수라 따로 둔다.
import { isSameAnswer } from "./bookActivities";

// 지인에듀가 종이와 같은 배치(cells, clues)를 준다. 단어가 놓인 칸은 번호 칸에서 가로·세로로 이어지는 칸이다.
export function crosswordWordList(crossword) {
  const letterOf = {};
  (crossword.cells || []).forEach((cell) => {
    letterOf[`${cell.row}:${cell.col}`] = cell;
  });
  const startOf = {};
  (crossword.cells || []).forEach((cell) => {
    if (cell.number) startOf[cell.number] = cell;
  });
  const wordList = [];
  ["across", "down"].forEach((direction) => {
    ((crossword.clues || {})[direction] || []).forEach((clue) => {
      const start = startOf[clue.number];
      if (!start) return;
      const cellList = [];
      for (let index = 0; index < String(clue.word || "").length; index += 1) {
        const row = start.row + (direction === "down" ? index : 0);
        const col = start.col + (direction === "across" ? index : 0);
        const cell = letterOf[`${row}:${col}`];
        if (!cell) break;
        cellList.push(cell);
      }
      wordList.push({ ...clue, direction, cellList });
    });
  });
  return wordList;
}

// ───────── 채점 ─────────
// 활동 하나를 채점해 문항별 결과 목록을 돌려준다. null 은 채점하지 않는 문항이다.
export const gradeActivity = (activity, response) => {
  const value = response || {};
  switch (activity.kind) {
    case "choice":
      return activity.items.map((item, index) =>
        item.answerIndex >= 0 ? value[index] === item.answerIndex : null
      );
    case "input":
      return activity.items.map((item, index) =>
        item.gradeable && item.answer ? isSameAnswer(value[index], item.answer) : null
      );
    case "written":
    case "table":
      return activity.items.map(() => null);
    case "match":
      return activity.items.flatMap((item, itemIndex) =>
        item.pairs.map((_, leftIndex) => ((value[itemIndex] || {})[leftIndex] ?? -1) === leftIndex)
      );
    case "order":
      return activity.items.map((item, itemIndex) => {
        const pickedList = value[itemIndex] || [];
        return pickedList.length === item.correctList.length && pickedList.every((picked, index) => picked === index);
      });
    case "multi":
      return activity.items.map((item, itemIndex) => {
        const pickedList = [...(value[itemIndex] || [])].sort();
        const answerList = [...item.answerIndexList].sort();
        return pickedList.length === answerList.length && pickedList.every((picked, index) => picked === answerList[index]);
      });
    case "classify":
      return activity.items.flatMap((item, itemIndex) =>
        item.rowList.map((row, rowIndex) => ((value[itemIndex] || {})[rowIndex] ?? -1) === row.answerIndex)
      );
    case "gap":
      return activity.items.flatMap((item, itemIndex) =>
        item.answerList.map((answer, blankIndex) => isSameAnswer((value[itemIndex] || {})[blankIndex], answer))
      );
    case "crossword":
      return crosswordWordList(activity.crossword).map((word) =>
        word.cellList.every((cell) => ((value.letters || {})[`${cell.row}:${cell.col}`] || "").toLowerCase() === cell.letter.toLowerCase())
      );
    case "word_search":
      return (activity.wordSearch.words || []).map((word) => (value.found || []).includes(word));
    default:
      return [];
  }
};

// ───────── 해설지용 기록 ─────────
// gradeActivity 와 같은 순서·같은 개수로 문항마다 {prompt, input, answer, explanation} 을 만든다.
// 서버의 해설지 PDF(utils/pdf/comprehensive/book_element.py)는 활동 모양을 몰라도 이것만 찍으면 된다.
const joinText = (list, separator = " / ") => list.filter((one) => one !== undefined && one !== null && one !== "").join(separator);

export const reportActivity = (activity, response) => {
  const value = response || {};
  switch (activity.kind) {
    case "choice":
      return activity.items.map((item, index) => ({
        itemIndex: index,
        quote: item.quote || "",
        prompt: item.prompt || "",
        options: item.options,
        isInline: Boolean(activity.isInline),
        pickedIndexList: value[index] !== undefined ? [value[index]] : [],
        answerIndexList: item.answerIndex >= 0 ? [item.answerIndex] : [],
        input: value[index] !== undefined ? item.options[value[index]] : "",
        answer: item.answerIndex >= 0 ? item.options[item.answerIndex] : "",
        explanation: item.explanation || "",
        evidence: item.evidence || "",
      }));
    case "input":
    case "written":
      return activity.items.map((item, index) => ({
        itemIndex: index,
        quote: item.quote || "",
        prompt: item.prompt || "",
        subPrompt: item.subPrompt || "",
        isLetterPuzzle: Boolean(item.isLetterPuzzle),
        helpList: item.helpList || [],
        rubricList: item.rubricList || [],
        input: value[index] || "",
        answer: item.answer || "",
        explanation: item.explanation || "",
        evidence: item.evidence || "",
      }));
    case "table":
      return activity.items.map((item, itemIndex) => {
        const filled = value[itemIndex] || {};
        return {
          itemIndex,
          prompt: item.prompt || "",
          headerList: item.headerList,
          tableRowList: item.rowList.map((row, rowIndex) => ({
            label: row.label,
            answerList: row.answerList,
            inputList: item.headerList.map((_, column) => filled[`${rowIndex}:${column}`] || ""),
          })),
          input: item.rowList
            .map((row, rowIndex) => `${row.label}: ${joinText(item.headerList.map((_, column) => filled[`${rowIndex}:${column}`]))}`)
            .join("<br/>"),
          answer: item.rowList.map((row) => `${row.label}: ${joinText(row.answerList)}`).join("<br/>"),
          explanation: "",
        };
      });
    case "match":
      return activity.items.flatMap((item, itemIndex) =>
        item.pairs.map((pair, leftIndex) => {
          const picked = (value[itemIndex] || {})[leftIndex];
          return {
            itemIndex,
            itemPrompt: item.prompt || "",
            prompt: pair.left,
            input: picked !== undefined ? item.pairs[picked].right : "",
            answer: pair.right,
            explanation: "",
          };
        })
      );
    case "order":
      return activity.items.map((item, itemIndex) => {
        const separator = activity.isSentence ? " " : " → ";
        return {
          itemIndex,
          prompt: item.prompt || "",
          isSentence: Boolean(activity.isSentence),
          correctList: item.correctList,
          pickedIndexList: value[itemIndex] || [],
          input: (value[itemIndex] || []).map((index) => item.correctList[index]).join(separator),
          answer: item.correctList.join(separator),
          explanation: item.explanation || "",
        };
      });
    case "multi":
      return activity.items.map((item, itemIndex) => ({
        itemIndex,
        prompt: item.prompt,
        options: item.options,
        pickedIndexList: value[itemIndex] || [],
        answerIndexList: item.answerIndexList,
        input: (value[itemIndex] || []).map((index) => item.options[index]).join("<br/>"),
        answer: item.answerIndexList.map((index) => item.options[index]).join("<br/>"),
        explanation: item.explanation || "",
      }));
    case "classify":
      return activity.items.flatMap((item, itemIndex) =>
        item.rowList.map((row, rowIndex) => {
          const picked = (value[itemIndex] || {})[rowIndex];
          return {
            itemIndex,
            itemPrompt: item.prompt || "",
            categoryList: item.categoryList,
            prompt: row.text,
            input: picked !== undefined ? item.categoryList[picked] : "",
            answer: item.categoryList[row.answerIndex] || "",
            explanation: "",
          };
        })
      );
    case "gap":
      return activity.items.flatMap((item, itemIndex) =>
        item.answerList.map((answer, blankIndex) => ({
          itemIndex,
          text: item.text,
          bankList: item.bankList,
          prompt: `(${blankIndex + 1})`,
          input: (value[itemIndex] || {})[blankIndex] || "",
          answer,
          explanation: "",
        }))
      );
    case "crossword":
      return crosswordWordList(activity.crossword).map((word) => ({
        prompt: `${word.number} ${word.direction === "across" ? "가로" : "세로"}. ${word.clue}`,
        direction: word.direction,
        number: word.number,
        clue: word.clue,
        input: word.cellList.map((cell) => ((value.letters || {})[`${cell.row}:${cell.col}`] || "_")).join(""),
        answer: String(word.word || "").toUpperCase(),
        explanation: "",
      }));
    case "word_search":
      return (activity.wordSearch.words || []).map((word) => ({
        prompt: word,
        input: (value.found || []).includes(word) ? "찾음" : "못 찾음",
        answer: "",
        explanation: "",
      }));
    default:
      return [];
  }
};
